import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const SMOOTH_GROUP_SELECTOR = '[data-smooth-single-open-accordion]';
const ANIMATION_DURATION = 260;
const ANIMATION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** @type {WeakMap<HTMLElement, () => void>} */
const activeAnchorCleanups = new WeakMap();

/**
 * Finds the nearest vertical scroll container.
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
function getScrollParent(element) {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;

    if (/(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return /** @type {HTMLElement} */ (
    document.scrollingElement || document.documentElement
  );
}

/**
 * Keeps the clicked summary in the same viewport position while an
 * already-open accordion above it collapses.
 *
 * This runs only for the duration of the accordion transition.
 *
 * @param {HTMLElement} summary
 * @param {HTMLElement} group
 * @returns {() => void}
 */
function keepSummaryAnchored(summary, group) {
  activeAnchorCleanups.get(group)?.();

  const scrollParent = getScrollParent(summary);
  const anchorTop = summary.getBoundingClientRect().top;

  /*
   * Horizon uses smooth scrolling on the page scroll container.
   * Repeated scrollTop corrections while that is active cause the visible
   * bounce/jitter. Disable smooth scrolling only for this short transition,
   * then restore the element's original inline value.
   */
  const originalInlineScrollBehavior = scrollParent.style.scrollBehavior;
  scrollParent.style.scrollBehavior = 'auto';

  let disposed = false;
  let frameId = 0;

  const stabilize = () => {
    if (disposed || !summary.isConnected) return;

    const currentTop = summary.getBoundingClientRect().top;
    const delta = currentTop - anchorTop;

    if (Math.abs(delta) > 0.5) {
      scrollParent.scrollTop = scrollParent.scrollTop + delta;
    }

    frameId = requestAnimationFrame(stabilize);
  };

  const cleanup = () => {
    if (disposed) return;

    cancelAnimationFrame(frameId);

    // One last synchronous correction while smooth scrolling is still disabled.
    if (summary.isConnected) {
      const finalTop = summary.getBoundingClientRect().top;
      const delta = finalTop - anchorTop;

      if (Math.abs(delta) > 0.5) {
        scrollParent.scrollTop = scrollParent.scrollTop + delta;
      }
    }

    disposed = true;

    if (activeAnchorCleanups.get(group) === cleanup) {
      activeAnchorCleanups.delete(group);
    }

    // Restore whatever inline scroll behavior existed before this interaction.
    if (originalInlineScrollBehavior) {
      scrollParent.style.scrollBehavior = originalInlineScrollBehavior;
    } else {
      scrollParent.style.removeProperty('scroll-behavior');
    }
  };

  activeAnchorCleanups.set(group, cleanup);
  frameId = requestAnimationFrame(stabilize);

  return cleanup;
}

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Animation | null} */
  #contentAnimation = null;

  /** @type {Promise<void>} */
  #transitionPromise = Promise.resolve();

  #transitionId = 0;

  /** @type {boolean | null} */
  #targetOpen = null;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    const content = this.details.querySelector(':scope > .details-content');

    return content instanceof HTMLElement ? content : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #smoothGroup() {
    const group = this.closest(SMOOTH_GROUP_SELECTOR);

    return group instanceof HTMLElement ? group : null;
  }

  get isOpenOrOpening() {
    return this.#targetOpen ?? this.details.open;
  }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#targetOpen = this.details.open;

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;

    this.#contentAnimation?.cancel();
    this.#contentAnimation = null;
  }

  /**
   * Handles the click event.
   * Native Horizon behavior is preserved everywhere except inside the
   * opt-in product accordion group.
   *
   * @param {Event} event
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    const group = this.#smoothGroup;

    // Menus, filters, cart-note accordions, etc. keep Horizon's native behavior.
    if (!group) return;

    event.preventDefault();

    const shouldOpen = !this.isOpenOrOpening;

    if (!shouldOpen) {
      this.setOpen(false);
      return;
    }

    const otherAccordions = Array.from(
      group.querySelectorAll(':scope > accordion-custom')
    ).filter(
      (accordion) =>
        accordion instanceof AccordionCustom &&
        accordion !== this &&
        accordion.isOpenOrOpening
    );

    const stopAnchoring =
      otherAccordions.length > 0
        ? keepSummaryAnchored(this.summary, group)
        : null;

    const transitions = otherAccordions.map((accordion) => accordion.setOpen(false));
    transitions.push(this.setOpen(true));

    Promise.allSettled(transitions).finally(() => {
      stopAnchoring?.();
    });
  };

  /**
   * Smoothly sets this accordion's open state.
   * @param {boolean} open
   * @param {boolean} [animate=true]
   * @returns {Promise<void>}
   */
  setOpen(open, animate = true) {
    if (
      this.#targetOpen === open &&
      this.#contentAnimation
    ) {
      return this.#transitionPromise;
    }

    this.#targetOpen = open;
    const transitionId = ++this.#transitionId;

    const details = this.details;
    const content = this.content;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!content || !animate || reduceMotion) {
      this.#contentAnimation?.cancel();
      this.#contentAnimation = null;
      details.open = open;
      this.#cleanupContentStyles(content);
      this.#transitionPromise = Promise.resolve();
      return this.#transitionPromise;
    }

    // Capture the currently rendered height before cancelling/reversing an animation.
    const startHeight = details.open
      ? content.getBoundingClientRect().height
      : 0;

    this.#contentAnimation?.cancel();
    this.#contentAnimation = null;

    if (open && !details.open) {
      details.open = true;
    }

    // Pin the current visual height in the same frame so reversing/starting
    // cannot flash to the natural content height.
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';
    content.style.willChange = 'height';

    const endHeight = open ? content.scrollHeight : 0;

    if (Math.abs(endHeight - startHeight) < 0.5) {
      details.open = open;
      this.#cleanupContentStyles(content);
      this.#transitionPromise = Promise.resolve();
      return this.#transitionPromise;
    }

    const animation = content.animate(
      [
        { height: `${startHeight}px` },
        { height: `${endHeight}px` },
      ],
      {
        duration: ANIMATION_DURATION,
        easing: ANIMATION_EASING,
        fill: 'forwards',
      }
    );

    this.#contentAnimation = animation;

    this.#transitionPromise = animation.finished
      .then(() => {
        if (transitionId !== this.#transitionId) return;

        if (!open) {
          details.open = false;
        }

        animation.cancel();

        if (this.#contentAnimation === animation) {
          this.#contentAnimation = null;
        }

        this.#cleanupContentStyles(content);
      })
      .catch(() => {
        // A cancelled animation is expected when the customer clicks rapidly.
      });

    return this.#transitionPromise;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#contentAnimation?.cancel();
    this.#contentAnimation = null;

    this.#setDefaultOpenState();
    this.#targetOpen = this.details.open;
    this.#cleanupContentStyles(this.content);
  };

  /**
   * Sets the default open state of the accordion based on the
   * `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.#targetOpen = false;
      this.summary.focus();
    }
  };

  /**
   * Clears temporary animation-only inline styles.
   * @param {HTMLElement | null} content
   */
  #cleanupContentStyles(content) {
    if (!content) return;

    content.style.removeProperty('height');
    content.style.removeProperty('overflow');
    content.style.removeProperty('will-change');
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
